import { ref } from "vue";
import { defineStore } from "pinia";
import { watchTriggerable } from "@vueuse/core";

import { captureException, captureMessage } from "@sentry/browser";

import { useAttributesStore } from "./attributes";
import OfflineReading, {
  OfflineMetadata,
} from "@/services/offline/OfflineService";

import { Navigator } from "@/components/navigator/types";
import { invokeAsync } from "@/helpers/async";
import { useWpsStore } from "./wps";
import { DEVICE_ID_KEY } from "@/wps/device";
import { serializeSession } from "@/wps/helpers";
import { makeOfflineChecksum } from "@/helpers/drm";

export const useOfflineStore = defineStore("offline", () => {
  const offlineLoading = ref(false);
  const offlineActive = ref(false);
  const offlineProgress = ref(0);

  const wpsStore = useWpsStore();
  const attributesStore = useAttributesStore();

  const { trigger } = watchTriggerable(
    () =>
      [wpsStore.client.token.allowOffline, wpsStore.sessionPath] as [
        boolean,
        string | null,
      ],
    async ([enabled, path]) => {
      if (!enabled) {
        offlineActive.value = false;
        return;
      }

      if (!path) {
        offlineActive.value = false;
        return;
      }
      // Check if the publication is already offline
      const workingOffline = await OfflineReading.check(path);
      if (workingOffline) {
        offlineActive.value = true;
      }
    },
    { immediate: true }
  );

  const goOffline = async (navigator?: Navigator): Promise<boolean> => {
    let result = false;
    try {
      offlineLoading.value = true;

      const wpsClient = wpsStore.client;

      // Already made available offline
      const session = await wpsClient.session();
      if (await OfflineReading.check(session.path)) {
        // act like it worked
        return true;
      }

      const estimate = await window.navigator.storage?.estimate?.();
      // TODO replace with more accurate estimate based on publication size
      if (estimate && estimate.usage + 50 * 1024 * 1024 > estimate.quota) {
        captureMessage("Less than 50MB available on device", {
          level: "info",
          extra: {
            usage: estimate.usage,
            quota: estimate.quota,
          },
        });
        return false;
      }

      // Force session refresh
      const serializedSession = serializeSession(await wpsClient.session(true));

      // Store metadata
      await OfflineReading.store({
        init: {
          device: window.localStorage.getItem(DEVICE_ID_KEY),
          token: session.refresh.token,
          type: wpsClient.token.type,
        },
        session: serializedSession,
        finalized: false,
      } as OfflineMetadata);

      // ensure the manifest is available
      if (!(await wpsClient.fetchResource("/manifest.json")).ok) {
        return false;
      }

      await navigator.prepareOffline(
        (numerator: number, denominator: number) => {
          // console.log(`Offline download progress: ${numerator}/${denominator}`);
          offlineProgress.value = Math.round((numerator / denominator) * 100);
          if (numerator === denominator) {
            offlineActive.value = true;
            result = true;
          }
        }
      );

      // finalize metadata
      await OfflineReading.store({
        init: {
          device: window.localStorage.getItem(DEVICE_ID_KEY),
          token: session.refresh.token,
          type: wpsClient.token.type,
        },
        session: serializedSession,
        finalized: true,
        checksum: await makeOfflineChecksum(
          `${session.id}|${wpsClient.token.type}|${session.refresh.token}`
        ),
      } as OfflineMetadata);
    } catch (e) {
      console.error("Error while going offline:", e);
      offlineProgress.value = 0;
      captureException(e);
    } finally {
      offlineLoading.value = false;
    }
    return result;
  };

  return {
    goOffline,
    offlineActive,
    offlineLoading,
    offlineProgress,
    $reset: () => {
      offlineActive.value = false;
      offlineLoading.value = false;
      offlineProgress.value = 0;
      invokeAsync(trigger);
    },
  };
});
